import React, { useState } from 'react';
import PageTitle from '../../components/PageTitle';
import { DownloadIcon } from '../../layout/icons';
import appIcon from '../../resources/images/schick-app-icon.png';
import logoDark from '../../resources/images/schick-logo-dark.png';
import logoWhite from '../../resources/images/schick-logo-white.png';
import { BRAND, DARK, modulePath, qrGeometry, qrPng, qrSvg } from './qrArt';
import { QR_CODES } from './qrCodes';
import './Material.css';

const LOGOS = [
  { key: 'dark', title: 'Logo schwarz', note: 'PNG, transparent · 600 × 502 px', src: logoDark, file: 'schick-logo-schwarz.png', stage: 'light' },
  { key: 'white', title: 'Logo weiß', note: 'PNG, transparent · 600 × 502 px · für dunkle Flächen', src: logoWhite, file: 'schick-logo-weiss.png', stage: 'dark' },
  { key: 'icon', title: 'App-Icon', note: 'PNG · 1024 × 1024 px', src: appIcon, file: 'schick-app-icon.png', stage: 'icon' },
];

const QRS = [
  {
    key: 'smart',
    file: 'schick-qr-app',
    title: 'App laden – iPhone & Android',
    lines: [
      'iPhone: direkt in den App Store.',
      'Android: bis zum Start im Play Store die Webseite mit „Bald auch für Android“, danach automatisch der Play Store. Der Code bleibt gültig.',
      'Laptop: die Webseite mit dem Download-Fenster.',
    ],
  },
  {
    key: 'appStore',
    file: 'schick-qr-app-store',
    title: 'Nur App Store (iPhone)',
    lines: [
      'Führt immer direkt zum App-Store-Eintrag von Schick Vienna.',
      'Android-Handys sehen dort nur die App-Store-Webseite.',
    ],
  },
];

const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const loadImage = (src) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error('Logo konnte nicht geladen werden.'));
  img.src = src;
});

const dataUrl = async (src) => {
  const blob = await (await fetch(src)).blob();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(new Error('Logo konnte nicht geladen werden.'));
    reader.readAsDataURL(blob);
  });
};

/** The code as the downloads draw it, for the page. */
function QrPreview({ rows, label }) {
  const g = qrGeometry(rows);
  return (
    <svg className="mat-qr__svg" viewBox={`0 0 ${g.size} ${g.size}`} role="img" aria-label={label}>
      <rect width={g.size} height={g.size} fill="#ffffff" />
      <path fill={DARK} shapeRendering="crispEdges" d={modulePath(g.modules)} />
      <rect x={g.logo.x} y={g.logo.y} width={g.logo.side} height={g.logo.side} rx={g.logo.radius} fill={BRAND} />
      <image x={g.mark.x} y={g.mark.y} width={g.mark.width} height={g.mark.height} href={logoWhite} />
    </svg>
  );
}

function QrCard({ qr }) {
  const { rows, url } = QR_CODES[qr.key];
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState('');

  const download = async (format) => {
    setBusy(format);
    setError('');
    try {
      if (format === 'svg') {
        const svg = qrSvg(rows, await dataUrl(logoWhite));
        saveBlob(new Blob([svg], { type: 'image/svg+xml' }), `${qr.file}.svg`);
      } else {
        saveBlob(await qrPng(rows, await loadImage(logoWhite)), `${qr.file}.png`);
      }
    } catch (err) {
      setError(err?.message || 'Download fehlgeschlagen.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <article className="mat-card mat-qr" aria-labelledby={`mat-qr-${qr.key}`}>
      <div className="mat-qr__stage"><QrPreview rows={rows} label={`QR-Code: ${url}`} /></div>
      <div className="mat-card__body">
        <h3 id={`mat-qr-${qr.key}`} className="mat-card__title">{qr.title}</h3>
        <a className="mat-qr__url" href={url} target="_blank" rel="noreferrer">{url.replace(/^https:\/\//, '')}</a>
        <ul className="mat-qr__lines">
          {qr.lines.map((line) => <li key={line}>{line}</li>)}
        </ul>
        <div className="mat-card__actions">
          <button type="button" className="mat-btn mat-btn--primary" onClick={() => download('svg')} disabled={!!busy}>
            <DownloadIcon /> {busy === 'svg' ? 'Erzeuge …' : 'SVG für Druck'}
          </button>
          <button type="button" className="mat-btn" onClick={() => download('png')} disabled={!!busy}>
            <DownloadIcon /> {busy === 'png' ? 'Erzeuge …' : 'PNG'}
          </button>
        </div>
        {error && <p className="mat-error" role="alert">{error}</p>}
      </div>
    </article>
  );
}

/** Logos and QR codes for flyers, posters, posts and partners, ready to download. */
export default function Material() {
  return (
    <div className="page mat">
      <div className="page-header">
        <PageTitle
          title="Material"
          description="Logos und QR-Codes für Flyer, Plakate, Posts und Partner. Ein Klick auf das Format lädt die Datei herunter."
        />
      </div>

      <section aria-labelledby="mat-logos">
        <h2 id="mat-logos" className="mat-section-title">Logos</h2>
        <div className="mat-logos">
          {LOGOS.map((logo) => (
            <article key={logo.key} className="mat-card" aria-labelledby={`mat-logo-${logo.key}`}>
              <div className={`mat-logo__stage mat-logo__stage--${logo.stage}`}>
                <img src={logo.src} alt={logo.title} />
              </div>
              <div className="mat-card__body">
                <h3 id={`mat-logo-${logo.key}`} className="mat-card__title">{logo.title}</h3>
                <p className="mat-card__note">{logo.note}</p>
                <div className="mat-card__actions">
                  <a className="mat-btn" href={logo.src} download={logo.file}><DownloadIcon /> PNG</a>
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section aria-labelledby="mat-qrs">
        <h2 id="mat-qrs" className="mat-section-title">QR-Codes</h2>
        <div className="mat-qrs">
          {QRS.map((qr) => <QrCard key={qr.key} qr={qr} />)}
        </div>
        <p className="mat-hint">
          Für den Druck das SVG nehmen – es bleibt in jeder Größe scharf. Mindestens 2 × 2 cm drucken, damit das Logo in der Mitte beim Scannen nicht stört.
        </p>
      </section>
    </div>
  );
}
